/* SWAPPING THE FILE ON A LAYER THAT ALREADY EXISTS.

   Deleting the layer and adding the new file back is not the same thing: it
   loses the placement, the size, the mask, the effects, the name — and every
   keyframe on it, which is the expensive half. A picture or a clip is the one
   part of a layer people re-supply (a better shot, a re-cut, a client's
   revision) while everything around it stays exactly as composed.

   What survives, by construction: position, rotation, scale, the width the
   layer was set to, tint, blend, effects, the mask, and every track. The
   HEIGHT is not preserved and should not be — both builders derive it from
   the source's own aspect, so the new file keeps its proportions inside the
   width already chosen.

   The mask survives an aspect change too: its points are normalised to image
   space (see buildImage's geomFor), so they describe the same region of a
   picture whatever shape it turns out to be. */

import * as THREE from 'three';
import { NICE, SHAPES } from '../core/doc.js';
import { localBounds } from './helpers.js';
import { putBlob } from '../services/media-store.js';
import { requestRender } from '../state/bus.js';
import { pushHistory } from '../state/history.js';
import { S } from '../state/store.js';
import { applyAnimation } from '../state/timeline.js';
import { parseModelFile, syncNode } from './objects.js';
import { dropVideoReader } from './video.js';
import { prepareVideo } from './video-import.js';

/** The name addObject would have given this object when it was created. */
const autoNamed = o =>
  new RegExp('^' + (NICE[o.type] || o.type) + ' \\d+$').test(o.name || '');

/* A layer named "Image 3" was never named by anyone — following the new file
   is more useful than keeping a number. A layer called "Client logo" was, and
   renaming that out from under someone is the kind of small theft that makes
   a tool feel untrustworthy. */
function renameFor(o, file) {
  if (!autoNamed(o)) return;
  o.name = file.name.replace(/\.[^.]+$/, '');
}

/* The old bytes are deliberately left in place. Nothing in the app collects
   orphaned blobs yet — media-store's sweep() has no callers, so deleting a
   video layer already strands its clip — and a replace is the wrong place to
   invent a garbage collector: the bytes it would drop may be the ones an undo
   is about to want back. */

async function replaceImage(o, file) {
  const src = await new Promise((resolve, reject) => {
    const rd = new FileReader();
    rd.onload = () => resolve(rd.result);
    rd.onerror = () => reject(new Error('That file could not be read.'));
    rd.readAsDataURL(file);
  });
  const P = o.params;
  P.src = src;
  /* Both of these have to go, and forgetting either is a bug you only see
     after a save and a reopen. hydrate() overwrites src FROM assetId, and
     dehydrate() drops src entirely whenever an assetId is present — so a
     stale id would quietly resurrect the old picture (core/doc.js). blobId
     goes with it for the same reason. */
  delete P.assetId;
  delete P.blobId;
  renameFor(o, file);
  syncNode(o);
}

async function replaceVideo(o, file) {
  // the same probe, limits, storage and upload an added clip goes through
  const { blobId, assetId, meta } = await prepareVideo(file);
  const P = o.params;
  P.blobId = blobId;
  if (assetId) P.assetId = assetId; else delete P.assetId;
  delete P.src;                       // a signed URL for the clip being replaced
  /* Cached metadata, not settings — and a pure function of the file, so it is
     rewritten wholesale. core/video-time.js reads these to decide which frame
     belongs at the playhead, and a stale duration answers confidently and
     wrongly. */
  P.srcDur = meta.seconds; P.srcW = meta.width; P.srcH = meta.height;
  P.srcFps = meta.fps; P.srcAlpha = meta.alpha;
  /* Timing is the user's, so it stays — except where the new clip cannot
     honour it. A trim of 8s into a 4s clip is not a preference to preserve,
     it is a state with no frames in it. */
  P.start = Math.max(0, Math.min(P.start || 0, Math.max(0, meta.seconds - 0.05)));
  /* The decoder is keyed on the object, not on the file, so it has to be
     handed back before the new bytes can be opened. */
  dropVideoReader(o.id);
  renameFor(o, file);
  syncNode(o);
  /* The frame showing right now belongs to the old clip. applyAnimation is the
     one funnel that puts a video at an instant (see core/video-time.js), so
     this asks the new reader for the frame that belongs at the playhead. */
  applyAnimation();
}

async function replaceModel(o, file) {
  // the same parse and the same auto-fit an imported model goes through
  const { src, center, fit, tris } = await parseModelFile(file);
  const P = o.params;
  P.src = src;
  delete P.assetId;                   // see replaceImage — a stale id resurrects the old file
  delete P.blobId;
  /* Its own centre and its own normalising scale, both properties OF THE FILE.
     o.scale is the layer's and is left alone, so a model swapped for another
     arrives at the same on-screen size and keeps whatever scaling was done to
     it — which is the whole point of swapping rather than re-importing. */
  P.center = center;
  P.fit = fit;
  P.tris = tris;
  renameFor(o, file);
  syncNode(o);
}

/* The size a shape takes up at its widest, in its own space — what the model
   replacing it is fitted to, so the real product lands at the size its
   stand-in was given. */
function shapeExtent(P) {
  switch (P.shape) {
    case 'sphere': return 2 * P.r;
    case 'cylinder': case 'cone': case 'capsule': return Math.max(2 * P.r, P.h);
    case 'torus': return 2 * (P.r + Math.min(P.tube, P.r));
    case 'plane': return Math.max(P.w, P.h);
    default: return Math.max(P.w, P.h, P.d);
  }
}

/* A SHAPE STANDING IN FOR A MODEL becomes that model. A cube or a cylinder is
   what a product is blocked in with before its file exists; when the file
   arrives it should take the shape's place, size, keyframes and name rather
   than be added beside it and lined up again by hand.

   The layer turns into a model layer: same id, same transform, same tracks.
   Its colour and finish go back to neutral, since a model brings its own
   materials and has no Look tab to change them in. Sized by measuring the
   model as built — its widest side is made the shape's widest side — rather
   than by repeating the importer's own fitting arithmetic here, which would
   drift the day that changed. */
async function replaceShapeWithModel(o, file) {
  const { src, center, fit, tris } = await parseModelFile(file);
  const extent = shapeExtent(o.params);
  const label = SHAPES[o.params.shape]?.label;
  const wasAutoNamed = !!label && new RegExp('^' + label + ' \\d+$').test(o.name || '');
  o.type = 'model';
  o.params = { src, center, fit, tris };
  o.color = '#ffffff';
  o.metalness = 0;
  o.roughness = .42;
  o.unlit = false;
  syncNode(o);
  const size = localBounds(o.node).getSize(new THREE.Vector3());
  const widest = Math.max(size.x, size.y, size.z);
  if (widest > 1e-9 && extent > 0) { o.params.fit = fit * extent / widest; syncNode(o); }
  if (wasAutoNamed) o.name = file.name.replace(/\.[^.]+$/, '');
  applyAnimation();
}

/**
 * Swap the file behind a layer, keeping everything else.
 *
 * Throws with a sentence worth showing — the caller turns it into a toast,
 * the same contract importVideo has.
 *
 * @param {object} o     the layer
 * @param {File}   file  the new picture, clip or model
 */
export async function replaceSource(o, file) {
  if (!o || !file) return;
  if (!S.objects.includes(o)) return;          // deleted while the picker was open
  if (o.type === 'image') await replaceImage(o, file);
  else if (o.type === 'video') await replaceVideo(o, file);
  else if (o.type === 'model') await replaceModel(o, file);
  else if (o.type === 'shape') await replaceShapeWithModel(o, file);
  else throw new Error('Only a picture, a clip, a model or a shape can have its file swapped.');
  pushHistory();
  requestRender();
}

/** What the picker should accept for this layer. */
export const acceptFor = o =>
  o.type === 'video' ? 'video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm'
  : o.type === 'model' || o.type === 'shape' ? '.glb,model/gltf-binary'
  : 'image/*';
